import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, UserPlus, FilterX, Bell } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { fmtDate, istToday } from "../api";
import { Card, PageHeader, StatusBadge, Table, EmptyState, Spinner, Button, Input, Select, Badge } from "../components/ui";

export default function Candidates() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const reviewMode = searchParams.get("review") === "1";
  const [rows, setRows] = useState(null);
  const todayStr = istToday();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState(reviewMode ? "" : todayStr);
  const [dateTo, setDateTo] = useState(reviewMode ? "" : todayStr);
  const [clinicId, setClinicId] = useState("");
  const [clinics, setClinics] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [onlyReview, setOnlyReview] = useState(reviewMode);

  const canFilterClinic = ["client", "admin", "accounts"].includes(user.role);
  const canFilterClient = user.role === "clinic";

  const load = useCallback(() => {
    const params = {};
    const searching = search.trim().length > 0;
    const hasNonDateFilter = searching || Boolean(status) || Boolean(clinicId) || Boolean(clientId);
    const isDefaultTodayRange = dateFrom === todayStr && dateTo === todayStr;
    if (searching) params.search = search;
    if (status) params.status = status;
    // The page defaults to today's date. When another filter is selected, do not
    // accidentally hide matching historical candidates behind that default range.
    // Explicitly selected non-today date ranges continue to be respected.
    const ignoreDefaultDateRange = hasNonDateFilter && isDefaultTodayRange;
    if (!ignoreDefaultDateRange && dateFrom) params.date_from = dateFrom;
    if (!ignoreDefaultDateRange && dateTo) params.date_to = dateTo;
    if (clinicId) params.clinic_id = clinicId;
    if (clientId) params.client_id = clientId;
    if (user.role === "doctor" && onlyReview) params.needs_review = true;
    api.get("/candidates", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [search, status, dateFrom, dateTo, clinicId, clientId, onlyReview, user.role]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (canFilterClinic) api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
    if (canFilterClient) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [canFilterClinic, canFilterClient]);

  const clear = () => { setSearch(""); setStatus(""); setDateFrom(""); setDateTo(""); setClinicId(""); setClientId(""); setOnlyReview(false); };

  return (
    <div>
      <PageHeader
        title={user.role === "doctor" ? "Verification Cases" : "Candidates"}
        subtitle={search.trim() ? "Searching all candidates till date (date filter ignored)" : dateFrom === todayStr && dateTo === todayStr ? "Showing today's candidates — clear filters to see all" : "Search by name, INDOS, CDC, mobile or date of birth"}
      >
        {user.role === "clinic" && (
          <Link to="/candidates/new">
            <Button data-testid="register-candidate-btn"><UserPlus size={16} /> Register Candidate</Button>
          </Link>
        )}
      </PageHeader>

      <Card className="mb-6 p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <div className="relative lg:col-span-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Name / INDOS / CDC / Mobile / Company ID / DOB"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="candidate-search-input"
            />
          </div>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-filter-select">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="fit">Accepted</option>
            <option value="temp_unfit">Further Evaluation Required</option>
            <option value="unfit">Not Recommended</option>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from-input" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to-input" />
          {canFilterClinic ? (
            <Select value={clinicId} onChange={(e) => setClinicId(e.target.value)} data-testid="clinic-filter-select">
              <option value="">All clinics</option>
              {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          ) : canFilterClient ? (
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="client-filter-select">
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          ) : (
            <Button variant="outline" onClick={clear} data-testid="clear-filters-btn"><FilterX size={15} /> Clear</Button>
          )}
        </div>
        <div className="mt-3 flex items-center gap-4">
          {user.role === "doctor" && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} className="accent-teal" data-testid="needs-review-toggle" />
              Only cases awaiting review
            </label>
          )}
          {canFilterClinic && (
            <button onClick={clear} className="flex items-center gap-1 text-sm font-semibold text-slate-500 transition-colors hover:text-navy" data-testid="clear-filters-btn">
              <FilterX size={14} /> Clear filters
            </button>
          )}
        </div>
      </Card>

      <Card>
        {rows === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : rows.length === 0 ? (
          <EmptyState text="No candidates match your filters" />
        ) : (
          <Table headers={user.role === "client" ? ["Candidate", "Company ID", "Rank", "Vessel", "Clinic", "Status", "Date", ""] : ["Candidate", "Company ID", "Rank", "Client", "Clinic", "Status", "Date", ""]}>
            {rows.map((c) => (
              <tr key={c.visit_id || c.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="candidate-row">
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.indos_number ? `INDOS ${c.indos_number}` : c.mobile}</p>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.company_id}</td>
                <td className="px-4 py-3 text-slate-600">{c.rank}</td>
                {user.role === "client" && <td className="px-4 py-3 text-slate-600">{c.vessel || "-"}</td>}
                {user.role !== "client" && <td className="px-4 py-3 text-slate-600">{c.client_name}</td>}
                <td className="px-4 py-3 text-slate-600">{c.clinic_name}</td>
                <td className="px-4 py-3">
                  {c.status === "pending" && (!c.client_org_id || !(c.service_ids || []).length) ? (
                    <Badge color="amber" data-testid={`incomplete-badge-${c.visit_id || c.id}`}>Registration Incomplete</Badge>
                  ) : (
                    <StatusBadge status={c.status} />
                  )}
                  {c.status === "pending" && c.report_uploaded !== undefined && c.client_org_id && (c.service_ids || []).length > 0 && (
                    <Badge color={c.report_uploaded ? "teal" : "amber"} className="ml-1.5" data-testid={`report-status-${c.visit_id || c.id}`}>
                      {c.report_uploaded ? "Report Uploaded" : "Report Pending"}
                    </Badge>
                  )}
                  {c.has_new_remark && <Bell size={14} fill="currentColor" className="bell-ring ml-1.5 inline text-red-600" data-testid={`new-remark-bell-${c.visit_id || c.id}`} />}
                  {c.reviewed_by && c.status !== "pending" && (
                    <p className="mt-1 text-[11px] text-slate-500" data-testid={`reviewed-by-${c.visit_id || c.id}`}>by {c.reviewed_by}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(c.updated_at)}</td>
                <td className="px-4 py-3">
                  {user.role === "clinic" && c.status === "pending" && (!c.client_org_id || !(c.service_ids || []).length) ? (
                    <Link to={`/candidates/${c.id}/edit?step=2`} className="whitespace-nowrap text-sm font-semibold text-amber-600 transition-colors hover:text-navy" data-testid={`resume-candidate-${c.visit_id || c.id}`}>Complete Registration</Link>
                  ) : (
                    <Link to={`/candidates/${c.id}`} className="text-sm font-semibold text-teal transition-colors hover:text-navy" data-testid={`open-candidate-${c.visit_id || c.id}`}>Open</Link>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
